import { paths } from "@/types/generated/api-schema";
import { IcpProfile } from "@/types/generated/IcpProfile.ts";
import { objectKeysToCamel } from "@/util/case.ts";

/** Where an audit link lands: `/audit/<token>`, and `/audit/<token>/edit`. */
export const AUDIT_PATH = "/audit";

type AuditProspectResponse =
    paths["/audit-prospect/{token}"]["get"]["responses"]["200"]["content"]["application/json"];

/**
 * What an audit link opens: one website's drafted profile, and the
 * vocabulary to edit it with. `rules` is the same shape a workspace's
 * profile has, so the editor's own helpers work on it.
 */
export type AuditProspect = Omit<AuditProspectResponse, "rules"> & {
    rules: IcpProfile;
};

/** The link is unknown, expired, or was never valid. One answer for all three. */
export class AuditLinkClosed extends Error {}

function url(token: string, suffix = ""): string {
    const base = (
        (import.meta.env.VITE_MANAGEMENT_API_URL as string | undefined) ?? ""
    ).replace(/\/$/, "");

    return `${base}/audit-prospect/${encodeURIComponent(token)}${suffix}`;
}

/**
 * These calls do not go through the Api slice (services/apis/api.ts): its
 * base query fetches a Cognito access token for each request and throws
 * without one, and a prospect has no account yet. The token in the link is
 * the whole credential.
 */
async function call(
    token: string,
    suffix: string,
    init: RequestInit,
): Promise<AuditProspect> {
    const response = await fetch(url(token, suffix), {
        ...init,
        headers: {
            accept: "application/json",
            ...(init.body ? { "content-type": "application/json" } : {}),
        },
    });
    if (response.status === 404) {
        throw new AuditLinkClosed();
    }
    if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
            message?: string;
        } | null;
        throw new Error(
            body?.message ?? "Something went wrong. Please try again.",
        );
    }

    // `rules` is stored in camelCase and its values are never keys, so
    // camelizing the whole answer leaves it as it is.
    return objectKeysToCamel(await response.json()) as AuditProspect;
}

/** `preview` reads without recording the open: for a viewer who is not the prospect. */
export function fetchAuditProspect(
    token: string,
    preview = false,
): Promise<AuditProspect> {
    return call(token, preview ? "?preview=1" : "", { method: "GET" });
}

export function saveAuditProspect(
    token: string,
    rules: IcpProfile,
): Promise<AuditProspect> {
    return call(token, "", { method: "PUT", body: JSON.stringify({ rules }) });
}

export function optOutOfAuditEmails(token: string): Promise<AuditProspect> {
    return call(token, "/opt-out", { method: "POST" });
}

/** The token in an audit path, or null on any other path. */
export function auditTokenOf(pathname: string): string | null {
    return (
        new RegExp(`^${AUDIT_PATH}/([0-9a-f]{64})(?:/edit)?/?$`).exec(
            pathname,
        )?.[1] ?? null
    );
}

export function auditPath(token: string, edit = false): string {
    return `${AUDIT_PATH}/${token}${edit ? "/edit" : ""}`;
}
